import type { Brand } from "@src/domain/entities/brand";
import {
  Organization,
  type IOrganizationProps,
} from "@src/domain/entities/organization";

const ORG_ID_STORAGE_PREFIX = "dm-admin:org-id:";

/** Case-insensitive organization key comparison for URL params vs API keys. */
export function organizationKeysMatch(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const left = a?.trim().toUpperCase();
  const right = b?.trim().toUpperCase();
  return Boolean(left && right && left === right);
}

export function normalizeOrganizationCacheKey(key: string): string {
  return key.trim().toUpperCase();
}

/** Survives browser refresh so org details can refetch without router state or list cache. */
export function persistOrganizationIdForKey(
  organizationKey: string,
  organizationId: number,
): void {
  if (!organizationKey.trim() || organizationId <= 0) return;
  try {
    sessionStorage.setItem(
      `${ORG_ID_STORAGE_PREFIX}${normalizeOrganizationCacheKey(organizationKey)}`,
      String(organizationId),
    );
  } catch {
    // sessionStorage unavailable (private mode, quota, etc.)
  }
}

export function readOrganizationIdForKey(organizationKey: string): number | null {
  try {
    const raw = sessionStorage.getItem(
      `${ORG_ID_STORAGE_PREFIX}${normalizeOrganizationCacheKey(organizationKey)}`,
    );
    if (!raw) return null;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

/** Cached org is only usable when it has a stable id (avoids blank UI from stale empty cache). */
export function isUsableOrganization(
  org: Organization | null | undefined,
): org is Organization {
  return Boolean(org?.organizationId && org.organizationId > 0);
}

/** Reads getter-backed Organization fields for safe cloning (spread breaks class instances). */
export function toOrganizationProps(org: Organization): IOrganizationProps {
  return {
    organizationId: org.organizationId,
    organizationKey: org.organizationKey,
    organizationName: org.organizationName,
    organizationDisplayName: org.organizationDisplayName,
    organizationDescription: org.organizationDescription,
    organizationLogoUrl: org.organizationLogoUrl,
    organizationIsActive: org.organizationIsActive,
    organizationCreatedAt: org.organizationCreatedAt,
    organizationUpdatedAt: org.organizationUpdatedAt,
    organizationDeletedAt: org.organizationDeletedAt,
    organizationCreatedBy: org.organizationCreatedBy,
    organizationUpdatedBy: org.organizationUpdatedBy,
    organizationDeletedBy: org.organizationDeletedBy,
    brands: org.brands ?? [],
  };
}

export function cloneOrganizationWithBrands(
  org: Organization,
  brands: Brand[],
): Organization {
  return new Organization({
    ...toOrganizationProps(org),
    brands,
  });
}
